import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Loading } from '@bumble/app/components/Loading';
import { IOSSwitch } from '../IOSSwitch';

interface EditStaticIpProps {
  onSubmit: () => void;
  cpeId: string;
}

export const EditStaticIp = ({ onSubmit, cpeId }: EditStaticIpProps) => {
  const api = useApi();

  const { isLoading, data } = useQuery(
    ['cellularConfig', cpeId],
    () => api.getCellularConfigApiV1GetCellularConfigGet({ cpe_id: cpeId }),
    {
      enabled: !!cpeId,
      staleTime: 0,
      refetchOnMount: true,
    }
  );

  const [staticIpEnabled, setStaticIpEnabled] = useState<boolean>(false);
  const [disableSubmit, setDisableSubmit] = useState<boolean>(true);

  useEffect(() => {
    if (data?.data) {
      setStaticIpEnabled(data.data.static_ip ?? false);
      setDisableSubmit(true);
    }
  }, [data]);

  const editStaticIpPost = useMutation(api.configureCellularEndpointApiV1ConfigureCellularPost, {
    onSuccess() {
      toast.success(`${staticIpEnabled ? 'Enabled' : 'Disabled'} Static IP successfully.`);
      onSubmit();
    },
    onError: (error) => {
      apiError(error);
      setDisableSubmit(false);
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStaticIpEnabled(e.target.checked);
    setDisableSubmit(e.target.checked === (data?.data.static_ip ?? false));
  };

  const handleSubmit = () => {
    setDisableSubmit(true);
    editStaticIpPost.mutate({
      cpe_id: cpeId,
      static_ip: staticIpEnabled,
    });
  };

  if (isLoading) return <Loading />;

  return (
    <Box pt={2}>
      <Typography variant="body1" sx={{ mb: 6 }}>
        Enable or disable Bumblebee Static IP
      </Typography>
      <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
        <FormControlLabel
          value="top"
          control={
            <IOSSwitch color="primary" checked={staticIpEnabled} onChange={handleChange} />
          }
          label={staticIpEnabled ? 'Enabled' : 'Disabled'}
          labelPlacement="bottom"
          sx={(theme) => ({ '&:hover': { color: theme.palette.primary.main } })}
          componentsProps={{
            typography: {
              sx: (theme) => ({
                fontSize: 12,
                color: !staticIpEnabled ? theme.palette.grey[600] : theme.palette.primary.main,
              }),
            },
          }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit} disabled={disableSubmit || editStaticIpPost.isLoading}>
          Confirm
        </Button>
      </Box>
    </Box>
  );
};
